// Import only what we use
import {
  addVideoMainSlide,
  addVideoThumbnail,
  // updateVideoVisibility,
  syncVideosBetweenCarousels,
} from './CarouselManager';
import { togglePlayPause as toggleVideoPlayPause, initializeVimeoPlayers as initVimeoPlayers } from './PlayerControls';
import { generateVideoObserverCallback, generateVideoObserverOptions } from './VideoUtils';
import PDPVideo from './PDPVideoClass';

/**
 * Implementation of the PDPVideo class methods
 */

// Set the current context and reset state
PDPVideo.prototype.setContext = function (newContext) {
  if (!newContext) return;

  // Reset state
  this.vimeoPlayers = [];
  this.videoElements = [];
  this.currentVariantBarcode = null;

  // Update context and related elements
  this.context = newContext;
  this.mainCarousel = this.context.querySelector('[data-product-images-carousel]');
  this.thumbsCarousel = this.context.querySelector('[data-product-images-thumbs-wrapper]');
  this.mainSwiper = this.mainCarousel ? this.mainCarousel.querySelector('.swiper-main') : null;
  this.mainSwiperWrapper = this.mainSwiper ? this.mainSwiper.querySelector('.swiper-wrapper') : null;
  this.thumbsSwiper = this.thumbsCarousel ? this.thumbsCarousel.querySelector('.swiper') : null;
  this.thumbsSwiperWrapper = this.thumbsSwiper ? this.thumbsSwiper.querySelector('.swiper-wrapper') : null;
  this.videoDataElement = this.context.querySelector('[data-product-video-data]');
  this.productJsonElement = this.context.querySelector('[data-product-json]');
};

// Toggle play/pause state of a video
PDPVideo.prototype.togglePlayPause = function (button) {
  toggleVideoPlayPause(button, this.context);
};

// Initialize Vimeo players for external videos
PDPVideo.prototype.initializeVimeoPlayers = function () {
  this.vimeoPlayers = initVimeoPlayers(this.context);
  return this.vimeoPlayers;
};

// Get the metafield filename for a variant
PDPVideo.prototype.getMetafieldFilename = function (barcode) {
  // This would be the implementation for accessing metafields
  if (!this.productData || !this.productData.metafields || !this.productData.metafields.custom) {
    return null;
  }

  // Try to get the original video source from metafields
  const originalVideoSource = this.productData.metafields.custom.original_video_source;
  if (!originalVideoSource) return null;

  // Extract barcode from the filename format: [barcode]_[sort order]_[seo text].[file type]
  const originalVideoFiles = Array.isArray(originalVideoSource) ? originalVideoSource : [originalVideoSource];

  // Find a filename that matches the barcode
  const matchingFile = originalVideoFiles.find((filename) => {
    const parts = filename.split('_');
    return parts[0] === barcode;
  });

  return matchingFile || null;
};

// Process videos for a variant
PDPVideo.prototype.processVideosForVariant = function (variantBarcode) {
  if (!variantBarcode) {
    return;
  }

  // Store the current variant barcode
  this.currentVariantBarcode = variantBarcode;

  // Check if this variant has videos
  if (!this.videoData || !this.videoData.videos || this.videoData.videos.length === 0) {
    return;
  }

  // Filter videos for this variant
  const variantVideos = this.videoData.videos.filter((video) => video.barcode === variantBarcode);

  // Hide all video slides first
  this.hideAllVideoSlides();

  if (variantVideos.length === 0) {
    // No videos for this variant, ensure all are hidden
    return;
  }

  // Check if we already have video slides for this variant in the DOM
  const mainVideoSlides = this.context.querySelectorAll(
    `.swiper-main .swiper-slide[data-video-variant-barcode="${variantBarcode}"]`,
  );
  const thumbVideoSlides = this.context.querySelectorAll(
    `.swiper-thumbs .swiper-slide[data-video-variant-barcode="${variantBarcode}"]`,
  );

  // If we have main slides for this variant, show them
  if (mainVideoSlides.length > 0) {
    mainVideoSlides.forEach((slide) => {
      slide.style.display = '';
    });
  }
  // Otherwise, add the videos to the DOM
  else if (variantVideos.length > 0) {
    variantVideos.forEach((video) => {
      if (this.mainSwiperWrapper) {
        addVideoMainSlide(video, this.mainSwiperWrapper);
      }
    });
  }

  // If we have thumb slides for this variant, show them
  if (thumbVideoSlides.length > 0) {
    thumbVideoSlides.forEach((slide) => {
      slide.style.display = '';
    });
  }
  // Otherwise, add the videos to the DOM
  else if (variantVideos.length > 0) {
    variantVideos.forEach((video) => {
      if (this.thumbsSwiperWrapper) {
        addVideoThumbnail(video, this.thumbsSwiperWrapper, this.productData);
      }
    });
  }

  // Update carousels to reflect changes
  this.updateCarousels();
};

// Helper method to hide all video slides
PDPVideo.prototype.hideAllVideoSlides = function (context = document) {
  const allMainVideoSlides = context.querySelectorAll('.swiper-main .swiper-slide[data-video-variant-barcode]');
  const allThumbVideoSlides = context.querySelectorAll('.swiper-thumbs .swiper-slide[data-video-variant-barcode]');

  // Hide all video slides
  allMainVideoSlides.forEach((slide) => {
    slide.style.display = 'none';
  });

  allThumbVideoSlides.forEach((slide) => {
    slide.style.display = 'none';
  });
};

// Add videos to the DOM
PDPVideo.prototype.addVideosToDom = function (videos) {
  // Early exits if no videos
  if (!videos || videos.length === 0) {
    return;
  }

  const context = this.context;

  // If videos is an HTMLElement, it's the context
  if (videos instanceof HTMLElement) {
    videos = this.videoData.videos;
  }

  // Get references to the carousel wrappers
  const mainCarouselWrapper = this.mainSwiperWrapper || context.querySelector('.swiper-main .swiper-wrapper');
  const thumbnailCarouselWrapper = this.thumbsSwiperWrapper || context.querySelector('.swiper-thumbs .swiper-wrapper');

  // Add each video to the DOM
  videos.forEach((video) => {
    // First add to main carousel
    if (mainCarouselWrapper) {
      // Directly add the video to the carousel wrapper
      // The function will handle creating the DOM elements
      addVideoMainSlide(video, mainCarouselWrapper);
    }

    // Then add to thumbnails carousel
    if (thumbnailCarouselWrapper) {
      // Directly add the video thumbnail to the carousel wrapper
      addVideoThumbnail(video, thumbnailCarouselWrapper, this.productData);
    }
  });

  // Update the carousels after adding new slides
  this.updateCarousels();
};

// Update video visibility for a specific variant
PDPVideo.prototype.updateVideoVisibility = function (variantBarcode, context = this.context) {
  if (!variantBarcode) {
    variantBarcode = this.currentVariantBarcode;
  }

  if (!variantBarcode) {
    return;
  }

  // Store the current variant barcode
  this.currentVariantBarcode = variantBarcode;

  // Temporarily store the original context
  const originalContext = this.context;

  // Set the context for the duration of this method call
  if (context !== this.context) {
    this.context = context;
  }

  // First, hide all video slides
  this.hideAllVideoSlides(context);

  // Check if this variant has videos
  if (!this.videoData || !this.videoData.barcodes || !this.videoData.videos) {
    this.context = originalContext;
    return;
  }

  // Check if this variant has videos
  const hasVideos = this.videoData.barcodes.includes(variantBarcode);
  if (!hasVideos) {
    this.context = originalContext;
    return;
  }

  // Get all video slides (both main and thumbnails)
  const mainVideoSlides = context.querySelectorAll('.swiper-main .swiper-slide[data-video-variant-barcode]');
  const thumbVideoSlides = context.querySelectorAll('.swiper-thumbs .swiper-slide[data-video-variant-barcode]');

  // Show only videos that match the current variant in the main carousel
  let visibleMainVideosCount = 0;
  mainVideoSlides.forEach((slide) => {
    const slideBarcode = slide.getAttribute('data-video-variant-barcode');
    if (slideBarcode === variantBarcode) {
      slide.style.display = '';
      visibleMainVideosCount++;
    }
  });

  // Show only videos that match the current variant in the thumbs carousel
  let visibleThumbVideosCount = 0;
  thumbVideoSlides.forEach((slide) => {
    const slideBarcode = slide.getAttribute('data-video-variant-barcode');
    if (slideBarcode === variantBarcode) {
      slide.style.display = '';
      visibleThumbVideosCount++;
    }
  });

  // If no videos were made visible but this variant should have videos,
  // we need to add them to the DOM
  if (visibleMainVideosCount === 0 && visibleThumbVideosCount === 0 && hasVideos) {
    // Check and add videos to the DOM if needed
    this.checkAndAddVideosToDom(null, context);
  }

  // Verify video slides to ensure proper DOM structure
  this.verifyVideoSlidesAfterVariantChange(variantBarcode);

  // Sync videos between carousels
  this.syncVideosBetweenCarousels(variantBarcode);

  // Update carousels
  this.updateCarousels();

  // Restore the original context
  this.context = originalContext;
};

// Verify and fix video slides after variant change
PDPVideo.prototype.verifyVideoSlidesAfterVariantChange = function (variantBarcode) {
  if (!variantBarcode) {
    console.warn(
      '\ud83c\udfac No barcode provided to verifyVideoSlidesAfterVariantChange, using currentVariantBarcode:',
      this.currentVariantBarcode,
    );
    variantBarcode = this.currentVariantBarcode;
    if (!variantBarcode) {
      console.error('\ud83c\udfac Cannot verify video slides: no barcode available');
      return;
    }
  }

  // Store the current variant barcode
  this.currentVariantBarcode = variantBarcode;

  // Validate required data
  if (!this.videoData || !this.videoData.barcodes || !this.videoData.videos) {
    console.error('\ud83c\udfac Cannot verify video slides: missing video data structure');
    return;
  }

  // Check if this variant has videos
  const hasVideos = this.videoData.barcodes.includes(variantBarcode);
  if (!hasVideos) {
    return;
  }

  // Get all video slides (both main and thumbnails)
  const mainVideoSlides = this.context.querySelectorAll('.swiper-main .swiper-slide[data-video-variant-barcode]');
  const thumbVideoSlides = this.context.querySelectorAll('.swiper-thumbs .swiper-slide[data-video-variant-barcode]');

  // First, hide all video slides
  mainVideoSlides.forEach((slide) => {
    slide.style.display = 'none';
  });

  thumbVideoSlides.forEach((slide) => {
    slide.style.display = 'none';
  });

  // Show only videos that match the current variant
  let visibleMainVideosCount = 0;
  let visibleThumbVideosCount = 0;

  // Show videos that match the current variant in the main carousel
  mainVideoSlides.forEach((slide) => {
    const slideBarcode = slide.getAttribute('data-video-variant-barcode');
    if (slideBarcode === variantBarcode) {
      slide.style.display = '';
      visibleMainVideosCount++;
    }
  });

  // Show videos that match the current variant in the thumbs carousel
  thumbVideoSlides.forEach((slide) => {
    const slideBarcode = slide.getAttribute('data-video-variant-barcode');
    if (slideBarcode === variantBarcode) {
      slide.style.display = '';
      visibleThumbVideosCount++;
    }
  });

  // If no videos were made visible but this variant should have videos,
  // we need to add them to the DOM
  if (visibleMainVideosCount === 0 && visibleThumbVideosCount === 0) {
    const variantVideos = this.videoData.videos.filter((video) => video.barcode === variantBarcode);
    if (variantVideos.length > 0) {
      this.checkAndAddVideosToDom(variantVideos);

      // Update carousel state
      this.updateCarousels();
      return;
    }
  } else if (visibleMainVideosCount !== visibleThumbVideosCount) {
    // If the counts do not match, we have a synchronization issue - this needs to be fixed
    // Get the variant videos
    const variantVideos = this.videoData.videos.filter((video) => video.barcode === variantBarcode);

    // If we have more main videos than thumbnails, we need to add the missing thumbnails
    if (visibleMainVideosCount > visibleThumbVideosCount) {
      // Find which videos are missing thumbnails
      const mainSortOrders = Array.from(mainVideoSlides)
        .filter(
          (slide) =>
            slide.getAttribute('data-video-variant-barcode') === variantBarcode && slide.style.display !== 'none',
        )
        .map((slide) => slide.getAttribute('data-count'));

      const thumbSortOrders = Array.from(thumbVideoSlides)
        .filter(
          (slide) =>
            slide.getAttribute('data-video-variant-barcode') === variantBarcode && slide.style.display !== 'none',
        )
        .map((slide) => slide.getAttribute('data-count'));

      // Find the missing sort orders
      const missingSortOrders = mainSortOrders.filter((order) => !thumbSortOrders.includes(order));

      // Add the missing thumbnails
      if (missingSortOrders.length > 0 && this.thumbsSwiperWrapper) {
        missingSortOrders.forEach((sortOrder) => {
          const matchingVideo = variantVideos.find(
            (v) => parseInt(v.sort_order || 999, 10) === parseInt(sortOrder, 10) || v.id?.includes(`_${sortOrder}`),
          );

          if (matchingVideo) {
            this.addVideoThumbnail(matchingVideo);
          }
        });

        // Update carousel state
        this.updateCarousels();
      }
    }
    // If we have more thumbnails than main videos, we need to add the missing main videos
    else if (visibleThumbVideosCount > visibleMainVideosCount) {
      // Find which videos are missing main slides
      const mainSortOrders = Array.from(mainVideoSlides)
        .filter(
          (slide) =>
            slide.getAttribute('data-video-variant-barcode') === variantBarcode && slide.style.display !== 'none',
        )
        .map((slide) => slide.getAttribute('data-count'));

      const thumbSortOrders = Array.from(thumbVideoSlides)
        .filter(
          (slide) =>
            slide.getAttribute('data-video-variant-barcode') === variantBarcode && slide.style.display !== 'none',
        )
        .map((slide) => slide.getAttribute('data-count'));

      // Find the missing sort orders
      const missingSortOrders = thumbSortOrders.filter((order) => !mainSortOrders.includes(order));

      // Add the missing main videos
      if (missingSortOrders.length > 0 && this.mainSwiperWrapper) {
        missingSortOrders.forEach((sortOrder) => {
          const matchingVideo = variantVideos.find(
            (v) => parseInt(v.sort_order || 999, 10) === parseInt(sortOrder, 10) || v.id?.includes(`_${sortOrder}`),
          );

          if (matchingVideo) {
            this.addVideoMainSlide(matchingVideo);
          }
        });

        // Update carousel state
        this.updateCarousels();
      }
    }
  }

  // Always update carousels to ensure proper initialization
  this.updateCarousels();
};

// Sync videos between main carousel and thumbnail carousel
PDPVideo.prototype.syncVideosBetweenCarousels = function (variantBarcode) {
  if (!variantBarcode) {
    variantBarcode = this.currentVariantBarcode;
  }

  if (!variantBarcode) {
    return;
  }

  // Call the imported syncVideosBetweenCarousels function
  syncVideosBetweenCarousels(
    variantBarcode,
    this.mainSwiperWrapper,
    this.thumbsSwiperWrapper,
    this.videoData,
    this.productData,
  );

  // Dispatch sync complete event
  this.context.dispatchEvent(new CustomEvent(PDPVideo.VIDEO_EVENTS.SLIDES_SYNCED));

  // Update carousels
  this.updateCarousels();
};

// Add a video to the main carousel
PDPVideo.prototype.addVideoMainSlide = function (video) {
  const mainCarouselWrapper = this.mainSwiperWrapper || this.context.querySelector('.swiper-main .swiper-wrapper');
  addVideoMainSlide(video, mainCarouselWrapper);
};

// Add a video thumbnail to the thumbs carousel
PDPVideo.prototype.addVideoThumbnail = function (video) {
  const thumbsCarouselWrapper =
    this.thumbsSwiperWrapper || this.context.querySelector('.swiper-thumbs .swiper-wrapper');
  addVideoThumbnail(video, thumbsCarouselWrapper, this.productData);
};

// Update carousels after changing slides
PDPVideo.prototype.updateCarousels = function () {
  if (!this.context) return;

  // Get the swiper instances
  const mainSwiperEl = this.context.querySelector('.swiper-main');
  const thumbsSwiperEl = this.context.querySelector('.swiper-thumbs');

  if (!mainSwiperEl?.swiper || !thumbsSwiperEl?.swiper) return;

  // Update the swipers
  mainSwiperEl.swiper.update();
  mainSwiperEl.swiper.updateSlides();
  thumbsSwiperEl.swiper.update();
  thumbsSwiperEl.swiper.updateSlides();

  // Sync active slide
  const activeIndex = mainSwiperEl.swiper.activeIndex;
  if (thumbsSwiperEl.swiper.activeIndex !== activeIndex) {
    thumbsSwiperEl.swiper.slideTo(activeIndex, 0, false);
  }

  // Dispatch event when carousels are updated
  this.context.dispatchEvent(new CustomEvent(PDPVideo.VIDEO_EVENTS.CAROUSELS_UPDATED));
};

// Check and add videos to the DOM if needed
PDPVideo.prototype.checkAndAddVideosToDom = function (videos, context = document) {
  // Early exit if no video data
  if (!this.videoData || !this.videoData.videos || this.videoData.videos.length === 0) {
    return;
  }

  // Get the current variant barcode
  let currentBarcode = null;

  // For QuickView, prioritize the videos parameter if provided
  if (context !== document && videos && videos.length > 0) {
    currentBarcode = videos[0].barcode;
  } else {
    // For both PDP and QuickView, try to get barcode from ProductOptions data attribute
    const productOptions = context.querySelector('.ProductOptions');
    if (productOptions && productOptions.getAttribute('data-variant-barcode')) {
      currentBarcode = productOptions.getAttribute('data-variant-barcode');
    }
  }

  // If no barcode found, try other methods
  if (!currentBarcode) {
    currentBarcode = this.getCurrentVariantBarcode();
  }

  if (!currentBarcode) return;

  // Check for existing videos for this variant
  const existingVideos = context.querySelectorAll(`[data-video-variant-barcode="${currentBarcode}"]`);
  if (existingVideos.length > 0) {
    // Videos already exist, just update their visibility
    this.updateVideoVisibility(currentBarcode, context);
    return;
  }

  // No existing videos found, proceed with adding them
  const variantVideos = this.videoData.videos.filter((video) => video.barcode === currentBarcode);
  if (variantVideos.length === 0) return;

  // Add videos to DOM
  this.addVideosToDom(variantVideos);

  // Update visibility
  this.updateVideoVisibility(currentBarcode, context);
};

// Get the current variant barcode
PDPVideo.prototype.getCurrentVariantBarcode = function () {
  // Try to get from ProductOptions data attribute first
  const productOptions = this.context.querySelector('.ProductOptions');
  if (productOptions && productOptions.getAttribute('data-variant-barcode')) {
    const barcode = productOptions.getAttribute('data-variant-barcode');
    return barcode;
  }

  // Try to get from the selected variant buttons
  const selectedColorButton = this.context.querySelector(
    '[data-custom-option-selector="option1"] [data-custom-variant-selector].ProductOption__variant--selected',
  );
  const selectedSizeButton = this.context.querySelector(
    '[data-custom-option-selector="option2"] [data-custom-variant-selector].ProductOption__variant--selected',
  );

  // Check if the selected color button has a barcode attribute
  if (selectedColorButton && selectedColorButton.getAttribute('data-variant-barcode')) {
    return selectedColorButton.getAttribute('data-variant-barcode');
  }

  // Check if the selected size button has a barcode attribute
  if (selectedSizeButton && selectedSizeButton.getAttribute('data-variant-barcode')) {
    return selectedSizeButton.getAttribute('data-variant-barcode');
  }

  // Try to get it from the style number value
  const styleNumberValue = this.context.querySelector('[data-style-number-value]');
  if (styleNumberValue && styleNumberValue.textContent) {
    return styleNumberValue.textContent.trim();
  }

  // Try to get it from the variant ID element
  const variantIdElement = this.context.querySelector('[data-variant-id]');
  if (variantIdElement) {
    const variantId = variantIdElement.getAttribute('data-variant-id');
    if (this.productData && this.productData.variants) {
      const currentVariant = this.productData.variants.find((v) => v.id.toString() === variantId);
      if (currentVariant && currentVariant.barcode) {
        return currentVariant.barcode;
      }
    }
  }

  // If we have a stored barcode, use that
  if (this.currentVariantBarcode) {
    return this.currentVariantBarcode;
  }

  return null;
};

// Attach an IntersectionObserver to any video slides that don't already have one,
// so they pause when scrolled out of view instead of streaming over Cloudinary
// indefinitely. Dynamic slides created in CarouselManager are pre-marked with
// data-video-observed and are skipped here.
PDPVideo.prototype.observeInlineVideoSlides = function () {
  if (!this.context) return;
  const slides = this.context.querySelectorAll('.swiper-slide[data-video-variant-barcode]:not([data-video-observed])');
  if (!slides.length) return;

  const root = this.mainSwiper || null;
  slides.forEach((slide) => {
    slide.setAttribute('data-video-observed', 'true');
    const observer = new IntersectionObserver(generateVideoObserverCallback(), generateVideoObserverOptions(root));
    observer.observe(slide);
  });
};

// Check if Swiper is initialized and return a promise
PDPVideo.prototype.checkSwipersInitialized = function (context) {
  // Update context if provided
  if (context && context instanceof HTMLElement) {
    this.context = context;
  }

  return new Promise((resolve) => {
    const checkSwipers = () => {
      const mainSwiperEl = this.context.querySelector('.swiper-main');
      const thumbsSwiperEl = this.context.querySelector('.swiper-thumbs');

      // If both swipers are initialized, resolve the promise
      if (mainSwiperEl && mainSwiperEl.swiper && thumbsSwiperEl && thumbsSwiperEl.swiper) {
        resolve(true);
        return;
      }

      // Otherwise, check again after a short delay
      setTimeout(checkSwipers, 100);
    };

    // Start the check
    checkSwipers();
  });
};

export default PDPVideo;
